import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';
import api from '../services/api';

function SidebarIcon({ name }) {
  const paths = {
    dashboard: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="2" />
        <rect x="14" y="3" width="7" height="7" rx="2" />
        <rect x="3" y="14" width="7" height="7" rx="2" />
        <rect x="14" y="14" width="7" height="7" rx="2" />
      </>
    ),
    search: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-4-4" />
      </>
    ),
    logout: (
      <>
        <path d="M10 17l5-5-5-5M15 12H3" />
        <path d="M14 3h5a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-5" />
      </>
    )
  };

  return (
    <svg className="sidebar-svg" viewBox="0 0 24 24" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

function Sidebar() {
  const [categorias, setCategorias] = useState([]);
  const [permisos, setPermisos] = useState({});
  const [adminAbierto, setAdminAbierto] = useState(false);
  const [seguridadAbierto, setSeguridadAbierto] = useState(false);
  const [tercerosAbierto, setTercerosAbierto] = useState(false);
  const [procesoAbierto, setProcesoAbierto] = useState(true);
  const [webAbierto, setWebAbierto] = useState(true);

  // ✅ colapsado SOLO desktop
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebar-collapsed') === '1';
    } catch {
      return false;
    }
  });

  // ✅ buscador
  const [search, setSearch] = useState('');
  const searchRef = useRef(null);

  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(min-width: 861px)').matches;
  });

  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useSession();

  // Detecta desktop vs móvil (NO toca el comportamiento del drawer móvil)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 861px)');
    const onChange = (e) => setIsDesktop(Boolean(e.matches));

    try {
      mq.addEventListener('change', onChange);
    } catch {
      mq.addListener(onChange);
    }

    setIsDesktop(Boolean(mq.matches));
    return () => {
      try {
        mq.removeEventListener('change', onChange);
      } catch {
        mq.removeListener(onChange);
      }
    };
  }, []);

  // Solo desktop: aplica ancho del sidebar con CSS var (sin tocar Layout.jsx)
  useEffect(() => {
    if (!isDesktop) return;
    const w = collapsed ? '78px' : '296px';
    try {
      document.documentElement.style.setProperty('--sidebar-w', w);
      localStorage.setItem('sidebar-collapsed', collapsed ? '1' : '0');
    } catch {
      // ignore
    }
  }, [collapsed, isDesktop]);

  useEffect(() => {
    async function cargarCategorias() {
      try {
        const res = await api.get('/api/catalogo/categorias');
        setCategorias(res.data);
      } catch (err) {
        console.error('❌ Error al cargar categorías:', err);
      }
    }

    async function cargarPermisosUsuario() {
      try {
        if (user?.id) {
          const res = await api.get(`/api/permisos/usuario/${user.id}`);
          const mapa = {};
          res.data.forEach((p) => (mapa[p.permiso] = true));

          // ✅ Si es admin, asegurar accesos de seguridad, emisor y DAE
          if (user.rol === 'admin') {
            mapa['usuarios'] = true;
            mapa['permisos'] = true;
            mapa['catalogo_permisos'] = true;
            mapa['empresa_config'] = true; // Emisor (SRI)
            mapa['dae'] = true; // DAE listado/uso
            mapa['dae_admin'] = true; // DAE administración/CRUD
            mapa['web_admin'] = true; // Administración web
          }

          setPermisos(mapa);
        } else {
          setPermisos({});
        }
      } catch (err) {
        console.error('❌ Error al cargar permisos:', err);
      }
    }

    cargarCategorias();
    cargarPermisosUsuario();
  }, [user]);

  // ===========================
  // ✅ Helpers ESTABLES (fix ESLint)
  // ===========================

  const excluir = useMemo(() => ['no_usar', 'interno'], []);

  const puede = useCallback(
    (permiso) => user?.rol === 'admin' || Boolean(permisos?.[permiso]),
    [user?.rol, permisos]
  );

  const isActive = useCallback(
    (pathStart) => location.pathname.startsWith(pathStart),
    [location.pathname]
  );

  const term = useMemo(() => search.trim().toLowerCase(), [search]);

  const match = useCallback(
    (txt) => {
      if (!term) return true;
      return String(txt || '')
        .toLowerCase()
        .includes(term);
    },
    [term]
  );

  // ✅ tooltip solo cuando está colapsado en desktop
  const tip = useCallback(
    (label) => {
      const show = isDesktop && collapsed;
      return {
        title: show ? label : undefined,
        'data-tip': show ? label : undefined
      };
    },
    [isDesktop, collapsed]
  );

  // ===========================
  // Visibilidad secciones
  // ===========================

  const puedeVerSeguridad =
    user?.rol === 'admin' ||
    permisos['usuarios'] ||
    permisos['permisos'] ||
    permisos['catalogo_permisos'] ||
    permisos['empresa_config'];

  const puedeVerAdministracion =
    categorias.some((obj) => puede('catalogo_admin') || puede(`catalogo_${obj.categoria}`)) ||
    puede('dae') ||
    puede('dae_admin');

  const itemsWeb = useMemo(() => {
    const out = [];

    if ((puede('web_admin') || user?.rol === 'admin') && match('Administrar Web')) {
      out.push({
        to: '/dashboard/web',
        icon: '🛠️',
        label: 'Administrar Web',
        active: isActive('/dashboard/web')
      });
    }

    return out;
  }, [match, puede, user?.rol, isActive]);

  // ===========================
  // Items (filtrables por búsqueda)
  // ===========================

  const itemsProceso = useMemo(() => {
    const out = [];
    if (puede('crear_pedido') && match('Crear Pedido')) {
      out.push({
        to: '/crear-pedido',
        icon: '📝',
        label: 'Crear Pedido',
        active: isActive('/crear-pedido')
      });
    }
    if (puede('facturas') && match('Facturas')) {
      out.push({ to: '/facturas', icon: '📁', label: 'Facturas', active: isActive('/facturas') });
    }
    if (puede('cartera') && match('Cartera')) {
      out.push({ to: '/cartera', icon: '📒', label: 'Cartera', active: isActive('/cartera') });
    }
    if (puede('reporte_facturas') && match('Reporte Facturas')) {
      out.push({
        to: '/reporte-factura',
        icon: '📊',
        label: 'Reporte Facturas',
        active: isActive('/reporte-factura')
      });
    }
    return out;
  }, [puede, match, isActive]);

  const itemsTerceros = useMemo(() => {
    const out = [];
    if (puede('clientes') && match('Clientes')) {
      out.push({
        to: '/terceros?tipo=cliente',
        icon: '🧑‍💼',
        label: 'Clientes',
        active: location.search.includes('cliente')
      });
    }
    if (puede('proveedores') && match('Proveedores')) {
      out.push({
        to: '/terceros?tipo=proveedor',
        icon: '🚛',
        label: 'Proveedores',
        active: location.search.includes('proveedor')
      });
    }
    return out;
  }, [puede, match, location.search]);

  const itemsAdminCatalogos = useMemo(() => {
    return categorias
      .map((obj) => obj.categoria)
      .filter((cat) => !excluir.includes(cat))
      .filter((cat) => puede('catalogo_admin') || puede(`catalogo_${cat}`))
      .map((cat) => ({
        key: cat,
        to: `/catalogo-admin?categoria=${encodeURIComponent(cat)}`,
        icon: '📌',
        label: cat.charAt(0).toUpperCase() + cat.slice(1),
        active:
          location.pathname === '/catalogo-admin' &&
          new URLSearchParams(location.search).get('categoria') === cat
      }))
      .filter((x) => match(x.label));
  }, [categorias, excluir, puede, match, location.pathname, location.search]);

  const itemDAE = useMemo(() => {
    if ((puede('dae') || puede('dae_admin')) && match('DAE')) {
      return { to: '/dae', icon: '📄', label: 'DAE', active: isActive('/dae') };
    }
    return null;
  }, [puede, match, isActive]);

  const itemsSeguridad = useMemo(() => {
    const out = [];
    if (puede('usuarios') && match('Usuarios'))
      out.push({ to: '/usuarios', icon: '👤', label: 'Usuarios', active: isActive('/usuarios') });
    if (puede('permisos') && match('Permisos'))
      out.push({ to: '/permisos', icon: '🔐', label: 'Permisos', active: isActive('/permisos') });
    if (puede('catalogo_permisos') && match('Catálogo de Permisos'))
      out.push({
        to: '/permisos-catalogo',
        icon: '📂',
        label: 'Catálogo de Permisos',
        active: isActive('/permisos-catalogo')
      });
    if ((user?.rol === 'admin' || permisos['empresa_config']) && match('Emisor'))
      out.push({
        to: '/config/emisor',
        icon: '🏷️',
        label: 'Emisor (SRI)',
        active: isActive('/config/emisor')
      });
    return out;
  }, [puede, match, isActive, user?.rol, permisos]);

  const showWeb = itemsWeb.length > 0;
  const showProceso = itemsProceso.length > 0;
  const showTerceros = itemsTerceros.length > 0;
  const showAdmin = puedeVerAdministracion && (itemsAdminCatalogos.length > 0 || itemDAE);
  const showSeg = puedeVerSeguridad && itemsSeguridad.length > 0;
  const showLogout = match('Cerrar sesión') || match('Cerrar sesion') || !term;

  const toggleCollapse = () => {
    if (!isDesktop) return; // solo pantallas grandes
    setCollapsed((v) => !v);
  };

  const onSearchIcon = () => {
    if (isDesktop && collapsed) {
      setCollapsed(false);
      setTimeout(() => searchRef.current?.focus?.(), 80);
      return;
    }
    searchRef.current?.focus?.();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/sistema', { replace: true });
  };

  return (
    <div className={collapsed && isDesktop ? 'sidebar collapsed' : 'sidebar'}>
      <div className="sidebar-header">
        <Link className="sidebar-brand" to="/dashboard" {...tip('Fresh Exports')}>
          <span className="sidebar-brand__mark">FE</span>
          <span className="sidebar-brand__copy ml">
            <strong>Fresh Exports</strong>
            <small>Gestión comercial</small>
          </span>
        </Link>

        {isDesktop && (
          <button
            type="button"
            className="sidebar-collapse"
            onClick={toggleCollapse}
            {...tip(collapsed ? 'Abrir menú' : 'Cerrar menú')}
            aria-label={collapsed ? 'Abrir menú' : 'Cerrar menú'}
          >
            {collapsed ? '›' : '‹'}
          </button>
        )}
      </div>

      <div className="sidebar-search">
        <button
          type="button"
          className="sidebar-search-icon"
          onClick={onSearchIcon}
          {...tip('Buscar servicios')}
          aria-label="Buscar servicios"
        >
          <SidebarIcon name="search" />
        </button>
        <input
          ref={searchRef}
          className="sidebar-search-input"
          placeholder="Buscar servicios"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <span className="sidebar-section-label ml">NAVEGACIÓN</span>
      {match('Dashboard') && (
        <Link
          className={`sidebar-dashboard-link ${location.pathname === '/dashboard' ? 'active-link' : ''}`}
          to="/dashboard"
          {...tip('Dashboard comercial')}
        >
          <span className="sidebar-nav-icon">
            <SidebarIcon name="dashboard" />
          </span>
          <span className="ml">Dashboard</span>
          <span className="sidebar-live ml">En vivo</span>
        </Link>
      )}

      <span className="sidebar-section-label sidebar-section-label--modules ml">MÓDULOS</span>

      {showWeb && (
        <>
          <button type="button" onClick={() => setWebAbierto(!webAbierto)} {...tip('Sitio Web')}>
            <span className="mi">🌐</span>
            <span className="ml">Sitio Web</span>
            <span className="chev">{webAbierto ? '▲' : '▼'}</span>
          </button>

          {webAbierto && (
            <ul>
              {itemsWeb.map((it) => (
                <li key={it.to}>
                  <Link className={it.active ? 'active-link' : ''} to={it.to} {...tip(it.label)}>
                    <span className="mi">{it.icon}</span>
                    <span className="ml">{it.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/* Proceso */}
      {showProceso && (
        <>
          <button
            type="button"
            onClick={() => setProcesoAbierto(!procesoAbierto)}
            {...tip('Operación comercial')}
          >
            <span className="mi">📦</span>
            <span className="ml">Operación comercial</span>
            <span className="chev">{procesoAbierto ? '▲' : '▼'}</span>
          </button>
          {procesoAbierto && (
            <ul>
              {itemsProceso.map((it) => (
                <li key={it.to}>
                  <Link className={it.active ? 'active-link' : ''} to={it.to} {...tip(it.label)}>
                    <span className="mi">{it.icon}</span>
                    <span className="ml">{it.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/* Terceros */}
      {showTerceros && (
        <>
          <button
            type="button"
            onClick={() => setTercerosAbierto(!tercerosAbierto)}
            {...tip('Terceros')}
          >
            <span className="mi">👥</span>
            <span className="ml">Terceros</span>
            <span className="chev">{tercerosAbierto ? '▲' : '▼'}</span>
          </button>
          {tercerosAbierto && (
            <ul>
              {itemsTerceros.map((it) => (
                <li key={it.to}>
                  <Link className={it.active ? 'active-link' : ''} to={it.to} {...tip(it.label)}>
                    <span className="mi">{it.icon}</span>
                    <span className="ml">{it.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/* Administración */}
      {showAdmin && (
        <>
          <button
            type="button"
            onClick={() => setAdminAbierto(!adminAbierto)}
            {...tip('Administración')}
          >
            <span className="mi">⚙️</span>
            <span className="ml">Administración</span>
            <span className="chev">{adminAbierto ? '▲' : '▼'}</span>
          </button>

          {adminAbierto && (
            <ul>
              {itemsAdminCatalogos.map((it) => (
                <li key={it.key}>
                  <Link className={it.active ? 'active-link' : ''} to={it.to} {...tip(it.label)}>
                    <span className="mi">{it.icon}</span>
                    <span className="ml">{it.label}</span>
                  </Link>
                </li>
              ))}

              {itemDAE && (
                <li>
                  <Link
                    className={itemDAE.active ? 'active-link' : ''}
                    to={itemDAE.to}
                    {...tip(itemDAE.label)}
                  >
                    <span className="mi">{itemDAE.icon}</span>
                    <span className="ml">{itemDAE.label}</span>
                  </Link>
                </li>
              )}
            </ul>
          )}
        </>
      )}

      {/* Seguridad */}
      {showSeg && (
        <>
          <button
            type="button"
            onClick={() => setSeguridadAbierto(!seguridadAbierto)}
            {...tip('Seguridad')}
          >
            <span className="mi">🔑</span>
            <span className="ml">Seguridad</span>
            <span className="chev">{seguridadAbierto ? '▲' : '▼'}</span>
          </button>
          {seguridadAbierto && (
            <ul>
              {itemsSeguridad.map((it) => (
                <li key={it.to}>
                  <Link className={it.active ? 'active-link' : ''} to={it.to} {...tip(it.label)}>
                    <span className="mi">{it.icon}</span>
                    <span className="ml">{it.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {showLogout && (
        <div className="sidebar-footer">
          <div className="sidebar-user" {...tip(user?.nombre || 'Usuario')}>
            <span className="sidebar-user__avatar">
              {(user?.nombre || 'U').trim().charAt(0).toUpperCase()}
            </span>
            <span className="sidebar-user__info ml">
              <strong>{user?.nombre || 'Usuario'}</strong>
              <small>{user?.rol === 'admin' ? 'Administrador' : user?.rol || 'Usuario'}</small>
            </span>
          </div>
          <button
            className="sidebar-logout"
            type="button"
            onClick={handleLogout}
            {...tip('Cerrar sesión')}
          >
            <SidebarIcon name="logout" />
            <span className="ml">Cerrar sesión</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default Sidebar;
